import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart drawer component.
 *
 * After every successful add-to-cart, the existing <theme-drawer>
 * is automatically opened.
 *
 * The actual drawer lifecycle, animations and positioning are handled
 * by <theme-drawer>.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Runs whenever the existing theme drawer opens.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const cta = this
          .querySelector('shopify-payment-terms')
          ?.shadowRoot
          ?.querySelector('#shopify-installments-cta');

        cta?.addEventListener(
          'click',
          () => this.#themeDrawer?.close(),
          { once: true }
        );
      });
  };

  /**
   * Automatically opens the existing cart drawer after
   * every successful Add to Cart.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const isAddToCart = event.action === 'add';

    if (!isAddToCart) {
      requestAnimationFrame(() => this.#updateStickyState());
      return;
    }

    /*
     * Check whether Add to Cart happened inside a modal,
     * for example Horizon Quick Add.
     *
     * If so, wait until that modal closes before opening
     * the cart drawer.
     */
    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    const openDrawer = () => {
      requestAnimationFrame(() => {
        if (!this.#themeDrawer?.isOpen) {
          this.#themeDrawer?.open();
        }

        this.#updateStickyState();
      });
    };

    /*
     * Wait for Shopify's cart request to finish.
     * This makes sure the drawer contains the newly added
     * product before/while it opens.
     */
    if (event.promise) {
      event.promise
        .then(({ detail }) => {
          /*
           * Don't open when Shopify reports an ATC error.
           */
          if (detail?.didError) {
            this.#updateStickyState();
            return;
          }

          /*
           * Quick Add / modal ATC:
           * first close the modal, then open the cart drawer.
           */
          if (sourceModal?.open) {
            sourceModal.addEventListener(
              'close',
              openDrawer,
              { once: true }
            );
          } else {
            openDrawer();
          }
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn(
              '[cart-drawer] Add to cart promise rejected:',
              error
            );
          }
        });

      return;
    }

    /*
     * Fallback in case the ATC event doesn't provide a promise.
     */
    if (sourceModal?.open) {
      sourceModal.addEventListener(
        'close',
        openDrawer,
        { once: true }
      );
    } else {
      openDrawer();
    }
  };

  /**
   * Updates whether the cart summary should stay sticky.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );
      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}